/**
 * Availability: the company's list, one person's with its approval, the employee's own.
 */
import { del, get, post, put } from '@/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ApiAvailability {
  id: number;
  user_id: number;
  start_date_time: string;
  end_date_time: string;
  is_available: boolean;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  recurrence_end_date: string | null;
  /** issue #70: a wish to work then, only on an available row */
  is_preference?: boolean;
  /** null when no approval was asked (the company's switch was off) */
  approval_status?: 'pending' | 'approved' | 'rejected' | null;
  decided_by?: number | null;
  decided_at?: string | null;
  /** what was approved before the employee changed it, kept while the change waits */
  approved_start_date_time?: string | null;
  approved_end_date_time?: string | null;
  approved_is_available?: boolean | null;
  approved_is_preference?: boolean | null;
  created_at?: string;
}
/** The body of POST /admin/users/:id/availabilities and PUT /admin/availabilities/:id (issue #70). */
export interface AvailabilityWrite {
  start_date_time: string;
  end_date_time: string;
  is_available: boolean;
  is_preference?: boolean;
  is_recurring?: boolean;
  recurrence_pattern?: 'weekly' | null;
  recurrence_end_date?: string | null;
}
/** Every availability row of the tenant (2 105 on tenant 1, counted 2026-09-09); the route has no date filter. */
export function useAvailabilities() {
  return useQuery({
    queryKey: ['availabilities'],
    queryFn: () => get<ApiAvailability[]>('/admin/availabilities'),
    staleTime: 5 * 60_000,
  });
}

/** One person's availability with its approval, for the employee page (issue #70). */
export function useUserAvailabilities(uid: number | null) {
  return useQuery({
    queryKey: ['user-availabilities', uid],
    queryFn: () => get<ApiAvailability[]>(`/admin/users/${uid}/availabilities`),
    enabled: uid != null,
    staleTime: 30_000,
  });
}
/** A manager's writes on one person's availability; the plan's list is refreshed with it. */
export function useAvailabilityAdminWrites(uid: number) {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['user-availabilities', uid] });
    qc.invalidateQueries({ queryKey: ['availabilities'] });
  };
  return {
    create: useMutation({
      mutationFn: (b: AvailabilityWrite) => post<unknown>(`/admin/users/${uid}/availabilities`, b),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: AvailabilityWrite }) =>
        put<ApiAvailability>(`/admin/availabilities/${id}`, body),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: ({ id, series }: { id: number; series?: boolean }) =>
        del<unknown>(`/admin/availabilities/${id}${series ? '?delete_series=true' : ''}`),
      onSettled: done,
    }),
    decide: useMutation({
      mutationFn: ({ id, ok, series }: { id: number; ok: boolean; series?: boolean }) =>
        put<ApiAvailability[]>(
          `/admin/availabilities/${id}/${ok ? 'approve' : 'reject'}${series ? '?series=true' : ''}`,
          {},
        ),
      onSettled: done,
    }),
  };
}
export function useMyAvailabilities() {
  return useQuery({
    queryKey: ['my-availabilities'],
    queryFn: () => get<ApiAvailability[]>('/availabilities'),
    staleTime: 30_000,
  });
}
